import React, { useEffect, useRef, useState } from "react";
import { Navbar, Nav, Button, Image, Container } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import { Link, useNavigate } from "react-router-dom";
import { FiChevronDown } from "react-icons/fi";
import Swal from "sweetalert2";
import { removeAuthedUser } from "../../redux/actions/AuthUserActions";
import { addToCart } from "../../redux/actions/CartActions";
import { spacePathFor } from "../../utils/roles";
import "./Navbar.css";

const servicesData = [
  { id: 1, title: "Création d'entreprise", from: "creation" },
  { id: 2, title: "Domiciliation d'entreprise", from: "domiciliation" },
  { id: 3, title: "Gestion 360°", from: "management" },
  { id: 4, title: "Administration d'entreprise", from: "admin" },
  { id: 7, title: "Suivi juridique", from: "juridique" },
  { id: 8, title: "Autres formalités", path: "/formalities" },
];

function AppNavbar() {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const { isAuthenticated, user } = useSelector((state) => state.authUser);
  const { userCompanies } = useSelector((state) => state.companies);

  const [isSticky, setIsSticky] = useState(false);
  const [openServices, setOpenServices] = useState(false);
  // Collapsed menu (phones and medium screens) open or closed
  const [expanded, setExpanded] = useState(false);

  const flyoutRef = useRef(null);

  useEffect(() => {
    const onScroll = () => setIsSticky(window.scrollY > 8);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // close flyout on outside click
  useEffect(() => {
    const onDocClick = (e) => {
      if (!flyoutRef.current) return;
      if (!flyoutRef.current.contains(e.target)) setOpenServices(false);
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, []);

  // Leaves the collapsed menu once a page is chosen
  const closeMenu = () => {
    setExpanded(false);
    setOpenServices(false);
  };

  const goTo = (path) => {
    closeMenu();
    navigate(path);
  };

  const handleLogout = () => {
    closeMenu();
    Swal.fire({
      title: "Voulez-vous vraiment vous déconnecter ?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#FF5500",
      cancelButtonColor: "#9AA0A6",
      confirmButtonText: "Confirmer",
      cancelButtonText: "Annuler",
    }).then((r) => {
      if (r.isConfirmed) dispatch(removeAuthedUser());
    });
  };

  const handleServiceClick = async (service) => {
    closeMenu();
    if (service.path) {
      navigate(service.path);
      return;
    }
    if (service.from) {
      await dispatch(addToCart({ from: service.from }));
      if (isAuthenticated && user) {
        const hasCompanies =
          Array.isArray(userCompanies) && userCompanies.length > 0;
        navigate(hasCompanies ? "/companies" : "/business-country");
      } else {
        navigate("/owner-information");
      }
    }
  };

  // Desktop hover open; mobile click toggle. Touch screens also fire
  // mouseenter before the click, which would close the menu at once.
  const canHover = () =>
    window.matchMedia?.("(hover: hover) and (min-width: 1200px)").matches;
  const toggleServices = () => setOpenServices((v) => !v);
  const openNow = () => canHover() && setOpenServices(true);
  const closeNow = () => canHover() && setOpenServices(false);

  const goToDash = () => {
    if (!user) return;
    goTo(user.role === "user" ? "/user" : spacePathFor(user.role));
  };

  return (
    <Navbar
      expand="xl"
      expanded={expanded}
      onToggle={setExpanded}
      className={`app-navbar home-navbar ${isSticky ? "is-sticky" : ""}`}
      aria-label="Navigation principale"
      style={{
        position: "sticky",
        top: 0,
        zIndex: 1000,
        ...(isSticky && { borderBottom: "1px solid #dcdcdc" }),
      }}
    >
      <Container>
        <Navbar.Brand as={Link} to="/" className="brand" onClick={closeMenu}>
          <Image
            src="/images/logo-Cplatform.png"
            alt="Platform98"
            width={200}
            height={55}
            className="brand-logo"
          />
        </Navbar.Brand>

        <Navbar.Toggle aria-controls="main-nav" className="nav-toggle" />
        <Navbar.Collapse id="main-nav">
          <Nav className="mx-auto nav-links">
            <Nav.Link as={Link} to="/" className="nav-link" onClick={closeMenu}>
              Accueil
            </Nav.Link>

            {/* Services as a real Nav.Link with flyout */}
            <Nav.Item
              className={`nav-item-with-flyout ${openServices ? "open" : ""}`}
              onMouseEnter={openNow}
              onMouseLeave={closeNow}
              ref={flyoutRef}
            >
              <Nav.Link
                role="button"
                className="nav-link has-dropdown"
                aria-haspopup="menu"
                aria-expanded={openServices}
                onClick={(e) => {
                  e.preventDefault();
                  toggleServices();
                }}
              >
                Nos services
                <FiChevronDown className="chevron" aria-hidden="true" />
              </Nav.Link>

              <div className="nav-flyout" role="menu">
                {servicesData.map((s) => (
                  <button
                    key={s.id}
                    className="flyout-item"
                    role="menuitem"
                    onClick={() => handleServiceClick(s)}
                  >
                    {s.title}
                  </button>
                ))}
              </div>
            </Nav.Item>

            <Nav.Link
              as={Link}
              to="/formalities"
              className="nav-link"
              onClick={closeMenu}
            >
              Formalités diverses
            </Nav.Link>
            <Nav.Link
              as={Link}
              to="/pricing"
              className="nav-link"
              onClick={closeMenu}
            >
              Nos tarifs
            </Nav.Link>
          </Nav>

          <Nav className="nav-actions">
            {isAuthenticated && user ? (
              <>
                <button
                  type="button"
                  className="nav-text-link"
                  onClick={handleLogout}
                >
                  Se déconnecter
                </button>
                <Button className="btn-orange nav-cta" onClick={goToDash}>
                  Tableau de bord
                </Button>
              </>
            ) : (
              <>
                <button
                  type="button"
                  className="nav-text-link"
                  onClick={() => goTo("/login")}
                >
                  Se connecter
                </button>
                <Button
                  className="btn-orange nav-cta"
                  onClick={() => goTo("/register")}
                >
                  Commencer maintenant
                </Button>
              </>
            )}
          </Nav>
        </Navbar.Collapse>
      </Container>
    </Navbar>
  );
}

export default AppNavbar;
